import type { ReportConfigBody, ReportKind } from '@palaccount/contracts';
import { Columns3, Save, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { useDeleteConfig, useReportConfigs, useSaveConfig } from './api';
import { moveColumn, resolveColumns, toggleColumn, type ColumnDef } from './column-picker';

/**
 * R13 in one panel: the column picker (BR-DSH-10-20 — tick per column,
 * reorder, delivered default first) and the saved configurations
 * (BR-DSH-10-22 — name, save, reopen, delete; personal to the signed-in
 * user). BR-DSH-10-23 needs no code here: a configuration replays CHOICES;
 * the data was already permission-filtered by the server.
 */

export interface DetailColumn<Row> {
  id: string;
  label: string;
  numeric?: boolean;
  render: (row: Row) => ReactNode;
}

export function DetailTable<Row>({
  report,
  columns,
  defaults,
  selected,
  onSelect,
  rows,
  rowKey,
  currentConfig,
  onApplyConfig,
}: {
  report: ReportKind;
  columns: DetailColumn<Row>[];
  defaults: string[];
  selected: string[];
  onSelect: (columns: string[]) => void;
  rows: Row[];
  rowKey: (row: Row) => string;
  /** What Save as configuration captures: filters + period from the page. */
  currentConfig: () => ReportConfigBody;
  /**
   * The NAME rides along so the page can print it in the heading block while
   * the configuration stays in force (BR-DSH-10-16, DSH-10 Band 6).
   */
  onApplyConfig: (config: ReportConfigBody, name: string) => void;
}) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const configs = useReportConfigs(report);
  const save = useSaveConfig(report);
  const remove = useDeleteConfig(report);

  const defs: ColumnDef[] = columns.map((c) => ({ id: c.id, labelKey: c.label }));
  const active = resolveColumns(defs, selected, defaults);
  const byId = new Map(columns.map((c) => [c.id, c]));

  return (
    <div className="flex flex-col gap-2">
      <div className="no-print flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPickerOpen((o) => !o)}>
          <Columns3 className="me-1 h-4 w-4" />
          {t('reports.columns')}
        </Button>
        <label className="ms-auto flex items-center gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('reports.savedConfigs')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value=""
            onChange={(e) => {
              const config = configs.data?.find((c) => c.id === e.target.value);
              if (config) {
                onApplyConfig(config.config, config.name);
                onSelect(config.config.columns);
              }
            }}
          >
            <option value="">{t('reports.pickConfig')}</option>
            {(configs.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const name = window.prompt(t('reports.configNamePrompt'));
            if (name) save.mutate({ name, config: { ...currentConfig(), columns: active } });
          }}
        >
          <Save className="me-1 h-4 w-4" />
          {t('reports.saveConfig')}
        </Button>
        {(configs.data ?? []).length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const name = window.prompt(t('reports.deleteConfigPrompt'));
              const config = configs.data?.find((c) => c.name === name);
              if (config) remove.mutate(config.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {pickerOpen ? (
        <div className="no-print grid gap-1 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-3">
          {columns.map((column) => {
            const at = active.indexOf(column.id);
            return (
              <div key={column.id} className="flex items-center gap-2 text-sm">
                <input
                  id={`col-${column.id}`}
                  type="checkbox"
                  checked={at >= 0}
                  onChange={() => onSelect(toggleColumn(active, column.id))}
                />
                <label htmlFor={`col-${column.id}`} className="flex-1">
                  {column.label}
                </label>
                {at >= 0 ? (
                  <span className="flex gap-1">
                    <button
                      type="button"
                      aria-label={t('reports.moveEarlier', { column: column.label })}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => onSelect(moveColumn(active, column.id, -1))}
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      aria-label={t('reports.moveLater', { column: column.label })}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => onSelect(moveColumn(active, column.id, 1))}
                    >
                      →
                    </button>
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <table aria-label={t('reports.viewDetailed')} className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-muted-foreground">
            {active.map((id) => (
              <th key={id} className={cn('pb-1', byId.get(id)?.numeric ? 'text-end' : 'text-start')}>
                {byId.get(id)?.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-t">
              {active.map((id) => (
                <TableCell key={id} className={cn('px-0', byId.get(id)?.numeric ? 'text-end' : 'text-start')}>
                  {byId.get(id)?.render(row)}
                </TableCell>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
      ) : null}
    </div>
  );
}
